import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Description as InvoiceIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";
import ImportDialog from "../components/ImportDialog";

interface Development {
  id: string;
  name: string;
}

interface DebtorRef {
  id: string;
  fullName: string;
  unitRef?: string;
  developmentName?: string;
  developmentId?: string;
}

interface DebtorInvoiceRow {
  id: string;
  debtorId: string;
  invoiceNo: string;
  invoiceDate: string;
  dueDate?: string;
  description?: string;
  amountIncl: number;
  amountPaid?: number;
  outstanding?: number;
  status: string;
  invoiceType?: string;
  debtor?: DebtorRef | null;
}

const DebtorInvoicesList: React.FC = () => {
  const router = useRouter();
  const [invoices, setInvoices] = useState<DebtorInvoiceRow[]>([]);
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDevelopment, setSelectedDevelopment] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState("");
  const [sortField, setSortField] =
    useState<keyof DebtorInvoiceRow>("invoiceDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [debtors, setDebtors] = useState<DebtorRef[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<DebtorInvoiceRow | null>(null);
  const [formData, setFormData] = useState({
    debtorId: "",
    developmentId: "",
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: new Date().toISOString().split("T")[0],
    description: "",
    amountExcl: 0,
    vatRate: 0.15,
    vatAmount: 0,
    amountIncl: 0,
    invoiceType: "installment",
    status: "pending",
  });

  useEffect(() => {
    fetchDevelopments();
    fetchDebtors();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [selectedDevelopment, statusFilter, invoiceTypeFilter]);

  const fetchDevelopments = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/developments`);
      if (response.ok) {
        setDevelopments(await response.json());
      }
    } catch (error) {
      console.error("Error fetching developments:", error);
    }
  };

  const fetchDebtors = async () => {
    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/debtors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setDebtors(await response.json());
      }
    } catch (error) {
      console.error("Error fetching debtors:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const params = new URLSearchParams();
      if (selectedDevelopment) params.set("developmentId", selectedDevelopment);
      if (statusFilter) params.set("status", statusFilter);
      if (invoiceTypeFilter) params.set("invoiceType", invoiceTypeFilter);

      const query = params.toString();
      const url = `${config.API_BASE_URL}/debtor-invoices${query ? `?${query}` : ""}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setInvoices(await response.json());
      }
    } catch (error) {
      console.error("Error fetching debtor invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: keyof DebtorInvoiceRow) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const filteredInvoices = invoices
    .filter((invoice) => {
      const term = searchTerm.toLowerCase();
      return (
        invoice.invoiceNo?.toLowerCase().includes(term) ||
        invoice.description?.toLowerCase().includes(term) ||
        invoice.debtor?.fullName?.toLowerCase().includes(term) ||
        invoice.debtor?.unitRef?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      let aVal: any = a[sortField] ?? "";
      let bVal: any = b[sortField] ?? "";
      if (sortField === "invoiceDate" || sortField === "dueDate") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const totals = filteredInvoices.reduce(
    (acc, invoice) => {
      acc.count += 1;
      acc.amount += invoice.amountIncl || 0;
      acc.paid += invoice.amountPaid || 0;
      acc.outstanding += invoice.outstanding || 0;
      return acc;
    },
    { count: 0, amount: 0, paid: 0, outstanding: 0 },
  );

  const formatDate = (date?: string) =>
    date
      ? new Date(date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const getStatusColor = (status?: string) => {
    switch ((status || "").toLowerCase()) {
      case "paid":
        return "success";
      case "partial":
        return "warning";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  const resetForm = () => {
    setFormData({
      debtorId: "",
      developmentId: "",
      invoiceNo: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: new Date().toISOString().split("T")[0],
      description: "",
      amountExcl: 0,
      vatRate: 0.15,
      vatAmount: 0,
      amountIncl: 0,
      invoiceType: "installment",
      status: "pending",
    });
  };

  const recalculateAmounts = (
    amountExcl: number,
    vatRate: number,
    invoiceType: string,
  ) => {
    const vatAmount = invoiceType === "vat" || vatRate > 0
      ? parseFloat((amountExcl * vatRate).toFixed(2))
      : 0;
    const amountIncl = parseFloat((amountExcl + vatAmount).toFixed(2));
    setFormData((prev) => ({
      ...prev,
      amountExcl,
      vatRate,
      vatAmount,
      amountIncl,
      invoiceType,
    }));
  };

  const handleSaveInvoice = async () => {
    if (!formData.debtorId || !formData.invoiceNo) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("vms_access_token");
      const url = selectedInvoice
        ? `${config.API_BASE_URL}/debtor-invoices/${selectedInvoice.id}`
        : `${config.API_BASE_URL}/debtors/${formData.debtorId}/invoices`;
      const response = await fetch(url, {
        method: selectedInvoice ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          amountPaid: selectedInvoice?.amountPaid || 0,
          outstanding:
            selectedInvoice?.outstanding ?? formData.amountIncl,
        }),
      });

      if (response.ok) {
        setCreateDialogOpen(false);
        setSelectedInvoice(null);
        resetForm();
        fetchInvoices();
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditInvoice = (invoice: DebtorInvoiceRow) => {
    setSelectedInvoice(invoice);
    setFormData({
      debtorId: invoice.debtorId,
      developmentId: "",
      invoiceNo: invoice.invoiceNo,
      invoiceDate: invoice.invoiceDate?.split("T")[0] || "",
      dueDate: invoice.dueDate?.split("T")[0] || "",
      description: invoice.description || "",
      amountExcl: Number(
        ((invoice.amountIncl || 0) - ((invoice as any).vatAmount || 0)).toFixed(2),
      ),
      vatRate: 0.15,
      vatAmount: (invoice as any).vatAmount || 0,
      amountIncl: invoice.amountIncl || 0,
      invoiceType: invoice.invoiceType || "installment",
      status: invoice.status || "pending",
    });
    setCreateDialogOpen(true);
  };

  const handleDeleteInvoice = async (invoice: DebtorInvoiceRow) => {
    if (!window.confirm(`Delete invoice ${invoice.invoiceNo}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/debtor-invoices/${invoice.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        fetchInvoices();
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
    }
  };

  return (
    <FinanceContainer>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Debtor Invoices
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedInvoice(null);
              resetForm();
              setCreateDialogOpen(true);
            }}
          >
            Capture Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Bulk Import Invoices
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Invoice Count
                </Typography>
                <Typography variant="h4">{totals.count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Invoiced Value
                </Typography>
                <Typography variant="h4">{formatCurrency(totals.amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Paid Value
                </Typography>
                <Typography variant="h4">{formatCurrency(totals.paid)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Outstanding
                </Typography>
                <Typography variant="h4">{formatCurrency(totals.outstanding)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search invoice no, debtor, unit, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Development</InputLabel>
                <Select
                  value={selectedDevelopment}
                  label="Development"
                  onChange={(e) => setSelectedDevelopment(e.target.value)}
                >
                  <MenuItem value="">All Developments</MenuItem>
                  {developments.map((development) => (
                    <MenuItem key={development.id} value={development.id}>
                      {development.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth>
                <InputLabel>Invoice Type</InputLabel>
                <Select
                  value={invoiceTypeFilter}
                  label="Invoice Type"
                  onChange={(e) => setInvoiceTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="installment">Installment</MenuItem>
                  <MenuItem value="interest">Interest</MenuItem>
                  <MenuItem value="vat">VAT</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "invoiceNo"}
                      direction={sortField === "invoiceNo" ? sortDirection : "asc"}
                      onClick={() => handleSort("invoiceNo")}
                    >
                      Invoice
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Debtor</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "invoiceDate"}
                      direction={sortField === "invoiceDate" ? sortDirection : "asc"}
                      onClick={() => handleSort("invoiceDate")}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Box sx={{ py: 4 }}>
                        <InvoiceIcon sx={{ fontSize: 44, color: "text.secondary", mb: 1 }} />
                        <Typography color="text.secondary">
                          No debtor invoices found.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => router.push(`/finance/debtors/${invoice.debtorId}`)}
                    >
                      <TableCell>
                        <Typography fontWeight={600}>{invoice.invoiceNo}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {invoice.description || "No description"}
                        </Typography>
                      </TableCell>
                      <TableCell>{invoice.debtor?.fullName || invoice.debtorId}</TableCell>
                      <TableCell>{invoice.debtor?.unitRef || "-"}</TableCell>
                      <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                      <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={invoice.invoiceType || "installment"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={invoice.status}
                          color={getStatusColor(invoice.status)}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(invoice.amountIncl || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(invoice.amountPaid || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(invoice.outstanding || 0)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditInvoice(invoice);
                          }}
                          title="Edit Invoice"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteInvoice(invoice);
                          }}
                          title="Delete Invoice"
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            router.push(`/finance/debtors/${invoice.debtorId}`);
                          }}
                          title="View Debtor"
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedInvoice ? "Edit Invoice" : "Capture Invoice"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Debtor</InputLabel>
                  <Select
                    value={formData.debtorId}
                    label="Debtor"
                    onChange={(e) => {
                      const debtor = debtors.find((item) => item.id === e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        debtorId: e.target.value,
                        developmentId: debtor?.developmentId || "",
                      }));
                    }}
                  >
                    {debtors.map((debtor) => (
                      <MenuItem key={debtor.id} value={debtor.id}>
                        {debtor.fullName} {debtor.unitRef ? `(${debtor.unitRef})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Invoice Number"
                  value={formData.invoiceNo}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, invoiceNo: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Invoice Type</InputLabel>
                  <Select
                    value={formData.invoiceType}
                    label="Invoice Type"
                    onChange={(e) =>
                      recalculateAmounts(formData.amountExcl, formData.vatRate, e.target.value)
                    }
                  >
                    <MenuItem value="installment">Installment</MenuItem>
                    <MenuItem value="interest">Interest</MenuItem>
                    <MenuItem value="vat">VAT</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Invoice Date"
                  value={formData.invoiceDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, invoiceDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Due Date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, dueDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount Excl"
                  value={formData.amountExcl}
                  onChange={(e) =>
                    recalculateAmounts(
                      Number(e.target.value || 0),
                      formData.vatRate,
                      formData.invoiceType,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="VAT Rate"
                  value={formData.vatRate}
                  onChange={(e) =>
                    recalculateAmounts(
                      formData.amountExcl,
                      Number(e.target.value || 0),
                      formData.invoiceType,
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Amount Incl"
                  value={formData.amountIncl}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveInvoice} disabled={saving}>
              {saving ? "Saving..." : selectedInvoice ? "Update Invoice" : "Save Invoice"}
            </Button>
          </DialogActions>
        </Dialog>

        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          type="debtor-invoices"
          onImportComplete={fetchInvoices}
        />
      </Box>
    </FinanceContainer>
  );
};

export default DebtorInvoicesList;
